import { Box, IconButton, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Grid from "@mui/material/Grid2";
import { useRef, useState } from 'react';
import { NivelPresionAnalisisEchometer, IprNodalAnalisis, DynacardsModule, AliModule } from '../dummies';

export type SecondaryTab = 'actualizarNivel' | 'cartas' | 'tendencias' | 'ipr' | 'ali';

const baseSecondaryTabs: { value: SecondaryTab; label: string; showOnlyForLiquidLevel?: boolean }[] = [
    { value: 'actualizarNivel', label: 'Actualizar Nivel', showOnlyForLiquidLevel: true },
    { value: 'cartas', label: 'Cartas' },
    { value: 'tendencias', label: 'Tendencias' },
    { value: 'ipr', label: 'IPR' },
    { value: 'ali', label: 'ALI' },
];

export interface DiagnosticoLeftPanelProps {
    activeTab: SecondaryTab;
    onTabChange: (tab: SecondaryTab) => void;
    mode?: 'liquidLevel' | 'dynCard' | 'generic';
    liquidLevel?: any;
    variablesToUpdate?: any;
    onChangeLiquidLevel?: (value: number, label: string) => void;
    /** When true, left panel shares space (50%). When false, left panel is full width. Used for Echometer responsive layout. */
    isCompact?: boolean;
}

export const DiagnosticoLeftPanel = ({
    activeTab,
    onTabChange,
    mode = 'generic',
    liquidLevel,
    variablesToUpdate,
    onChangeLiquidLevel = () => {},
    isCompact = true,
}: DiagnosticoLeftPanelProps) => {
    const secondaryTabs = baseSecondaryTabs.filter((t) => !t.showOnlyForLiquidLevel || mode === 'liquidLevel');
    const [nivelViewMode, setNivelViewMode] = useState<'classic' | 'echometer'>('classic');
    const subTabsScrollRef = useRef<HTMLDivElement>(null);

    const scrollSubTabs = (direction: 'left' | 'right') => {
        const el = subTabsScrollRef.current;
        if (el) {
            const scrollAmount = 150;
            el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'actualizarNivel':
                return (
                    <Box className="modal_analyst_body" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
                            <ToggleButtonGroup
                                size="small"
                                value={nivelViewMode}
                                exclusive
                                onChange={(_, v) => v && setNivelViewMode(v)}
                                aria-label="Vista de nivel"
                            >
                                <ToggleButton value="classic">Vista clásica</ToggleButton>
                                <ToggleButton value="echometer">Análisis Echometer TAM</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {nivelViewMode === 'classic' ? (
                            <Box sx={{ p: 2, overflow: 'auto' }}>
                                <Grid container spacing={2}>
                                    <Grid size={6}>
                                        <TextField
                                            size="small"
                                            label="Nivel (ft)"
                                            type="number"
                                            value={variablesToUpdate?.liquidLevel ?? liquidLevel?.liquidLevel ?? ''}
                                            onChange={(e) => onChangeLiquidLevel(parseFloat(e.target.value) || 0, 'liquidLevel')}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            size="small"
                                            label="PIP (psi)"
                                            type="number"
                                            value={variablesToUpdate?.pip ?? liquidLevel?.pip ?? ''}
                                            onChange={(e) => onChangeLiquidLevel(parseFloat(e.target.value) || 0, 'pip')}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            size="small"
                                            label="PWF (psi)"
                                            type="number"
                                            value={variablesToUpdate?.pwf ?? liquidLevel?.pwf ?? ''}
                                            onChange={(e) => onChangeLiquidLevel(parseFloat(e.target.value) || 0, 'pwf')}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField
                                            size="small"
                                            label="Sumergencia (ft)"
                                            type="number"
                                            value={variablesToUpdate?.pumpSumergence ?? liquidLevel?.pumpSumergence ?? ''}
                                            onChange={(e) => onChangeLiquidLevel(parseFloat(e.target.value) || 0, 'pumpSumergence')}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                <NivelPresionAnalisisEchometer
                                    liquidLevel={liquidLevel}
                                    variablesToUpdate={variablesToUpdate}
                                    onChangeLiquidLevel={onChangeLiquidLevel}
                                    isCompact={isCompact}
                                />
                            </Box>
                        )}
                    </Box>
                );
            case 'cartas':
                return (
                    <Box className="modal_analyst_body" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            <DynacardsModule isCompact={isCompact} />
                        </Box>
                    </Box>
                );
            case 'tendencias':
                return (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Contenido de Tendencias - Placeholder
                        </Typography>
                    </Box>
                );
            case 'ipr':
                return (
                    <Box className="modal_analyst_body" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            <IprNodalAnalisis isCompact={isCompact} />
                        </Box>
                    </Box>
                );
            case 'ali':
                return (
                    <Box className="modal_analyst_body" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            <AliModule isCompact={isCompact} />
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box className="left_panel_secondary_tabs" sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1, gap: 0, flexShrink: 0 }}>
                <IconButton size="small" onClick={() => scrollSubTabs('left')} sx={{ flexShrink: 0 }} aria-label="Scroll sub-tabs izquierda">
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Box
                    ref={subTabsScrollRef}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        display: 'flex',
                        flex: 1,
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    <ToggleButtonGroup
                        value={activeTab}
                        exclusive
                        onChange={(_, v) => v && onTabChange(v)}
                        aria-label="Sub-secciones"
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            backgroundColor: 'transparent',
                            border: 'none',
                            gap: 0,
                            flex: 1,
                            justifyContent: 'space-between',
                        }}
                    >
                        {secondaryTabs.map((tab) => (
                            <ToggleButton
                                key={tab.value}
                                value={tab.value}
                                sx={{
                                    border: 'none',
                                    fontSize: 12,
                                    color: activeTab === tab.value ? 'primary.main' : 'text.secondary',
                                    fontWeight: activeTab === tab.value ? 600 : 400,
                                    px: 1.25,
                                    py: 0.75,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    borderRadius: 0,
                                    borderBottom: activeTab === tab.value ? '2px solid' : 'none',
                                    borderColor: 'primary.main',
                                    '&.Mui-selected': {
                                        backgroundColor: 'transparent',
                                        color: 'primary.main',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    },
                                }}
                            >
                                {tab.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
                <IconButton size="small" onClick={() => scrollSubTabs('right')} sx={{ flexShrink: 0 }} aria-label="Scroll sub-tabs derecha">
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {renderTabContent()}
            </Box>
        </Box>
    );
};
